<template>
  <div
    class="list-filters page-filters w-100"
    :class="{ 'loading' : isLoadingData}">
    <!-- BASIC FILTERS -->
    <validation-observer
      ref="filtersObserver"
      tag="form"
      class="basic-filters d-flex flex-wrap align-items-center"
      @submit.prevent="applyFilters">

      <!-- Basic filter controls -->
      <div
        v-for="filter in basicFilters"
        :key="filter.key"
        class="position-relative d-flex align-items-center"
        :style="{ flex: '1 1 200px', maxWidth: filter.maxWidth || 'none' }">

        <filter-control
          v-model="localFilters[filter.key]"
          :filter="filter"
          :all-filters="localFilters"
          :disabled="isLoadingData || !!filter.disabled" />
      </div>

      <!-- Basic filter actions -->
      <div
        class="d-flex flex-wrap align-items-center filter-actions ml-auto"
        style="gap: 0.75rem;">
        <d-button
          theme="info"
          outline
          :disabled="isLoadingData"
          :title="$t('ui.search')"
          type="submit">
          <i class="material-icons">search</i>
        </d-button>

        <d-button
          v-if="advancedFilters.length > 0"
          theme="secondary"
          outline
          type="button"
          :disabled="isLoadingData"
          :title="$t('ui.advancedFilters')"
          @click.prevent="openAdvancedModal">
          <i class="material-icons">tune</i>
        </d-button>

        <d-button
          theme="danger"
          outline
          type="button"
          :disabled="isLoadingData"
          :title="$t('ui.resetFilters')"
          @click.prevent="resetFiltersAction">
          <i class="material-icons">clear</i>
        </d-button>
      </div>
    </validation-observer>

    <!-- BADGES: ACTIVE ADVANCED FILTERS -->
    <div
      v-if="activeAdvancedBadges.length"
      class="active-filters-badges mt-2">
      <d-badge
        v-for="badge in activeAdvancedBadges"
        :key="badge.key"
        theme="info"
        class="cursor-pointer p-2"
        :title="$t('ui.clickToRemove')"
        :disabled="isLoadingData"
        @click.native="removeBadge(badge.key)">
        <strong class="mr-1">{{ badge.label }}:</strong>&nbsp;{{ badge.displayValue }}
        <i class="material-icons">close</i>
      </d-badge>
    </div>

    <!-- Hidden filter controls to resolve entity select labels for badges -->
    <div
      v-if="!showAdvancedModal"
      style="display: none;">
      <filter-control
        v-for="filter in entitySelectAdvancedFilters"
        :key="'label-resolver-' + filter.key"
        :value="filters[filter.key]"
        :filter="filter"
        :all-filters="filters"
        :disabled="true"
        @display-label="label => $set(displayLabels, filter.key, label)" />
    </div>

    <!-- MODAL: ADVANCED FILTERS -->
    <div v-teleport>
      <d-modal
        v-if="showAdvancedModal"
        size="lg"
        @close="closeAdvancedModal">
        <d-modal-header>
          <d-modal-title>{{ $t('ui.advancedFilters') }}</d-modal-title>
        </d-modal-header>

        <d-modal-body>
          <validation-observer ref="advancedFiltersObserver">
            <d-row>
              <d-col
                v-for="filter in advancedFilters"
                :key="filter.key"
                sm="12"
                md="6"
                class="form-group d-flex">
                <filter-control
                  v-model="localFilters[filter.key]"
                  :filter="filter"
                  :all-filters="localFilters"
                  :disabled="isLoadingData || !!filter.disabled"
                  @display-label="label => $set(displayLabels, filter.key, label)" />
              </d-col>
            </d-row>
          </validation-observer>
        </d-modal-body>

        <d-modal-footer>
          <d-button
            theme="danger"
            outline
            class="mr-auto"
            @click="closeAdvancedModal">
            {{ $t('ui.cancel') }}
          </d-button>
          <d-button
            theme="success"
            :disabled="isLoadingData"
            @click="applyFilters">
            <i class="material-icons mr-1">check</i>
            {{ $t('order.useThisValue') }}
          </d-button>
        </d-modal-footer>
      </d-modal>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import HasFilters from '@/mixins/HasFilters.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';
import { mapGetters } from 'vuex';
import FilterControl from '@/components/common/FilterControl.vue';

export default {
  name: 'list-filters',
  mixins: [HasFilters],
  components: {
    'filter-control': FilterControl
  },
  directives: {
    teleport: {
      inserted(el) {
        document.body.appendChild(el);
      },
      unbind(el) {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    }
  },
  props: {
    /**
     * Array of configuration objects for individual filters.
     * Example filter object:
     * {
     *   key: 'status', // [Required] Unique filter key (used locally in v-model).
     *   apiKey: 'status_id', // [Optional] Key under which the value will be sent to the API (defaults to 'key').
     *   label: 'Status', // [Required] Label displayed to the user.
     *   placeholder: 'Select status', // [Optional] Placeholder text for input/select.
     *   type: 'select', // [Required] Control type: 'text' | 'select' | 'checkbox' | 'daterange' | 'customerSelect'.
     *   isBasic: true, // [Required] If true, the filter is displayed on the main bar. If false, it goes into the "Advanced filters" modal.
     *   defaultValue: null, // [Optional] Default value after resetting filters.
     *   permission: ['admin', 'worker'] || Function, // [Optional] Array of roles or a function returning a boolean that determines filter availability.
     *   description: 'Helpful description', // [Optional] Text displayed in a tooltip with information for the user.
     *   options: [], // [Required for type='select'] Array of options [{text, value}].
     *   multiple: false, // [Optional for type='select'] Whether multiple options can be selected.
     *   reduce: option => option.value, // [Optional for type='select'] Transformation of the selected value (returns value by default).
     *   searchable: true, // [Optional for type='select'] Whether options can be searched by typing text (true by default).
     *   maxWidth: '300px', // [Optional] Maximum width of the control on the main bar (defaults to 300px).
     *   loading: false, // [Optional] Passes the loading state to e.g., v-select (displays a spinner on the control).
     *   apiTransform: (val, where) => { where['custom'] = val; } // [Optional] Function for custom building of the API payload.
     * }
     */
    config: {
      required: true,
      type: Array
    },

    /**
     * Flag that disables saving filter state in localStorage.
     * Useful e.g. when the ListFilters component is used on the fly as a sub-view in a popup.
     */
    disableStorage: {
      default: false,
      type: Boolean
    },

    /**
     * Indicates whether the main data for the list (e.g. tables/API of the main view) is already loaded.
     * Controls the global ':disabled' state and the 'loading' class on all filters.
     */
    isListLoaded: {
      default: false,
      type: Boolean
    },

    /**
     * Used to build a unique key in localStorage (by the HasFilters mixin),
     * to distinguish the saved filter state of the same view opened in different popup variants.
     */
    popupFilter: {
      default: '',
      type: String
    },

    /**
     * Similar to popupFilter, modifies the localStorage key for different popup statuses.
     */
    popupStatus: {
      default: '',
      type: String
    },

    /**
     * Unique name of the view associated with this filter component.
     * Required for the HasFilters mixin to save the user's filter state under a unique key.
     */
    viewName: {
      required: true,
      type: String
    }
  },
  data () {
    return {
      displayLabels: {},
      filters: {},
      localFilters: {},
      showAdvancedModal: false
    };
  },
  computed: {
    ...mapGetters(['getUserType']),
    isLoadingData () {
      return !this.isListLoaded || !!this.config.find(item => ('loaded' in item) && !item.loaded);
    },
    currentViewNameForFilters () { // Override HasFilters mixin - use viewName prop instead of $options.name
      let name = this.viewName + '-' + this.$route.path.replace(/[^a-z0-9]/gmi, '');

      if (this.popupFilter) {
        name += '-' + this.popupFilter;
      }

      if (this.popupStatus) {
        name += '-' + this.popupStatus;
      }

      return name;
    },
    disableFiltersInSubview () { // Override HasFilters mixin computed
      return this.disableStorage;
    },
    visibleFiltersConfig () {
      return this.config.filter(filter => {
        if (!this.hasPermission(filter)) {
          return false;
        }
        if (typeof filter.visible === 'function') {
          return filter.visible(this.localFilters);
        }
        return true;
      });
    },
    basicFilters () {
      return this.visibleFiltersConfig.filter(filter => filter.isBasic);
    },
    advancedFilters () {
      return this.visibleFiltersConfig.filter(filter => !filter.isBasic);
    },
    entitySelectAdvancedFilters () {
      return this.advancedFilters.filter(f => ['customerSelect', 'agentSelect', 'userSelect'].includes(f.type) && !this.isValueEmpty(this.filters[f.key]));
    },
    activeAdvancedBadges () {
      let badges = [];
      this.advancedFilters.forEach(filter => {
        const val = this.filters[filter.key];
        if (this.isValueEmpty(val)) {
          return;
        }

        badges.push({
          key: filter.key,
          label: filter.label,
          displayValue: this.resolveDisplayValue(filter, val)
        });
      });

      return badges;
    }
  },
  watch: {
    localFilters: {
      handler (val) {
        this.$emit('update:filters', { ...val });
      },
      deep: true
    }
  },
  created () {
    let defaults = {};
    this.config.forEach(filter => {
      defaults[filter.key] = filter.defaultValue !== undefined ? cloneDeep(filter.defaultValue) : null;
    });

    this.filters = cloneDeep(defaults);
    this.localFilters = cloneDeep(defaults);
  },
  beforeMount () {
    // Mixin beforeMount runs first and loads saved filters into this.filters.
    // We must copy them to localFilters BEFORE children mount,
    // so that e.g. SelectCustomer receives the saved customerID on its own mount.
    this.localFilters = cloneDeep(this.filters);
  },
  mounted () {
    this.$emit('init', this.filters, this.getApiPayload());
  },
  methods: {
    hasPermission (filter) {
      if (!filter.permission) {
        return true;
      }

      if (typeof filter.permission === 'function') {
        return filter.permission();
      }

      const permissionsNeeded = Array.isArray(filter.permission)
        ? filter.permission
        : [filter.permission];

      return permissionsNeeded.some(perm => this.getUserType === perm);
    },
    openAdvancedModal () {
      if (this.isLoadingData) {
        return;
      }
      this.localFilters = cloneDeep(this.filters);
      this.showAdvancedModal = true;
    },
    closeAdvancedModal () {
      this.localFilters = cloneDeep(this.filters);
      this.displayLabels = {};
      this.showAdvancedModal = false;
    },
    async applyFilters () {
      const results = await Promise.all([
        this.$refs.filtersObserver ? this.$refs.filtersObserver.validate() : true,
        this.$refs.advancedFiltersObserver ? this.$refs.advancedFiltersObserver.validate() : true
      ]);

      if (results.some(valid => !valid)) {
        return;
      }

      this.filters = cloneDeep(this.localFilters);
      this.showAdvancedModal = false;

      if (!this.disableStorage) {
        this.saveFilters();
      }

      this.$emit('search', this.filters, this.getApiPayload());
    },
    resetFiltersAction () {
      if (!this.disableStorage) {
        this.resetFilters();
      } else {
        let defaults = {};
        this.config.forEach(filter => {
          defaults[filter.key] = filter.defaultValue !== undefined ? cloneDeep(filter.defaultValue) : null;
        });
        this.filters = cloneDeep(defaults);
      }

      this.localFilters = cloneDeep(this.filters);
      this.displayLabels = {};

      if (this.$refs.filtersObserver) {
        this.$refs.filtersObserver.reset();
      }
      if (this.$refs.advancedFiltersObserver) {
        this.$refs.advancedFiltersObserver.reset();
      }

      this.$emit('search', this.filters, this.getApiPayload());
    },
    removeBadge (key) {
      if (this.isLoadingData) {
        return;
      }
      const filterConfig = this.advancedFilters.find(filter => filter.key === key);
      const defaultVal = filterConfig && filterConfig.defaultValue !== undefined
        ? cloneDeep(filterConfig.defaultValue)
        : null;

      this.$set(this.filters, key, defaultVal);
      this.$set(this.localFilters, key, defaultVal);
      this.$delete(this.displayLabels, key);

      if (!this.disableStorage) {
        this.saveFilters();
      }

      this.$emit('search', this.filters, this.getApiPayload());
    },
    isValueEmpty (val) {
      if (val === null || val === undefined || val === '') {
        return true;
      }
      if (Array.isArray(val)) {
        return val.length === 0;
      }
      if (typeof val === 'object' && val.startDate === null && val.endDate === null) {
        return true;
      }
      return false;
    },
    resolveDisplayValue (filter, val) {
      if (['customerSelect', 'agentSelect', 'userSelect'].includes(filter.type)) {
        return this.displayLabels[filter.key] || val;
      }

      if (filter.type === 'select') {
        if (filter.optionLabel) {
          const getText = v => (typeof v === 'object' && v !== null) ? v[filter.optionLabel] : v;
          return Array.isArray(val) ? val.map(getText).join(', ') : getText(val);
        }

        if (Array.isArray(val)) {
          return val.map(v => this.getSelectOptionText(filter.options, v)).join(', ');
        }

        return this.getSelectOptionText(filter.options, val);
      }

      if (filter.type === 'daterange') {
        if (val.startDate && val.endDate) {
          return `${this.formatDate(val.startDate)} - ${this.formatDate(val.endDate)}`;
        }

        return this.$t('ui.chooseDate');
      }

      if (filter.type === 'toggle') {
        const option = (filter.options || []).find(opt => opt.value === val);
        return option ? option.text : val;
      }

      if (filter.type === 'checkbox') {
        return val ? this.$t('ui.yes') : this.$t('ui.no');
      }

      return val;
    },
    getSelectOptionText (options, value) {
      if (!options) {
        return value;
      }
      const option = options.find(opt => {
        const optValue = opt.value !== undefined ? opt.value : opt;
        return optValue === value;
      });

      return option ? (option.text || option.label || option) : value;
    },
    formatDate (date) {
      if (!date) {
        return '-';
      }

      return CalendarUtils.formatDateForPHP(new Date(date));
    },
    getApiPayload () {
      let where = {};

      this.visibleFiltersConfig.forEach(filter => {
        if (filter.disabled) {
          return;
        }

        let val = this.filters[filter.key];

        if (this.isValueEmpty(val)) {
          return;
        }

        if (typeof filter.apiTransform === 'function') {
          filter.apiTransform(val, where);
        } else {
          const apiKey = filter.apiKey || filter.key;
          where[apiKey] = val;
        }
      });

      return where;
    }
  }
};
</script>

<style lang="scss">
.list-filters {
  &.loading {
    opacity: 0.5;
  }
  .basic-filters {
    gap: 0.75rem;

    .btn > .material-icons {
      left: -2px;
      top: 4px;
    }

  }

  .active-filters-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;

    .badge {
      align-items: center;
      display: inline-flex;
      i.material-icons {
        font-size: 14px;
        margin-left: 0.5rem;
        top: 0;
      }

      &:hover {
        color: var(--red);
      }
    }
  }

  label.custom-toggle {
    margin-bottom: 0;
  }
}
</style>
